import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import './styles/LogoImage.css';

/**
 * LogoImage component - A clean, simple logo loader without any background artifacts
 * Uses standard img element with custom loading handling specifically for logos
 */
function LogoImage({ src, alt, className, style, ...props }) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const imgRef = useRef(null);

  const handleImageLoad = () => {
    setLoaded(true);
    setError(false);
    setLoading(false);
  };

  const handleImageError = () => {
    setError(true);
    setLoaded(false);
    setLoading(false);
  };

  const handleImageLoadStart = () => {
    setLoading(true);
    setLoaded(false);
    setError(false);
  };

  return (
    <div className={`logo-image-container ${className}`}>
      {/* Loading spinner - only show when loading */}
      {loading && !loaded && !error && (
        <div className="logo-loading-indicator">
          <div className="logo-spinner" />
        </div>
      )}

      {/* Logo image - completely hidden until loaded */}
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        onLoad={handleImageLoad}
        onError={handleImageError}
        onLoadStart={handleImageLoadStart}
        className="logo-main-image"
        style={{
          ...style,
          opacity: loaded ? 1 : 0,
          visibility: loaded ? 'visible' : 'hidden',
          transform: loaded ? 'translate(-50%, -50%) scale(1)' : 'translate(-50%, -50%) scale(0)',
        }}
        {...props}
      />

      {/* Error state */}
      {error && (
        <div className="logo-error-state">
          <div className="logo-error-icon">📷</div>
        </div>
      )}
    </div>
  );
}

LogoImage.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
};

LogoImage.defaultProps = {
  alt: 'Logo Image',
  className: '',
  style: {},
};

export default LogoImage;
